import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ApiError, type Envelope, type RefreshResponse } from './types'
import { tokenStore } from '../auth/tokenStore'

/** Base da API: runtime-config → env do Vite → proxy relativo. */
export function resolveApiBaseUrl(): string {
  return window.__TRANSGRAOS_CONFIG__?.apiBaseUrl ?? import.meta.env.VITE_API_BASE_URL ?? '/api/v1'
}

const baseURL = resolveApiBaseUrl()

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

// Cliente separado p/ o refresh (sem interceptors → sem recursão).
const refreshClient = axios.create({ baseURL, headers: { 'Content-Type': 'application/json' } })

// Handler de "sessão perdida" — o AuthContext registra p/ limpar user e ir ao /login.
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

// ── Request: injeta o Bearer do access token ─────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = tokenStore.getAccess()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// ── Refresh single-flight ────────────────────────────────────────────
let refreshPromise: Promise<string> | null = null

async function doRefresh(): Promise<string> {
  const refresh = tokenStore.getRefresh()
  if (!refresh) throw new ApiError('Sessão expirada.', 'UNAUTHORIZED', 401)
  const resp = await refreshClient.post<Envelope<RefreshResponse>>(
    '/auth/refresh',
    {},
    { headers: { Authorization: `Bearer ${refresh}` } },
  )
  const access = resp.data.data.access_token
  tokenStore.setAccess(access)
  return access
}

function forceLogout(): void {
  tokenStore.clear()
  onUnauthorized?.()
}

/** Renova o access token fora do interceptador, compartilhando o mesmo voo único.
 *
 *  O fluxo SSE do assistente abre um `fetch` cru, que não passa pelo axios nem
 *  pelo interceptador de 401. Ele renova o token ANTES de abrir a conexão, quando
 *  o access está perto de vencer, e é por isso que a renovação precisa existir
 *  como função exportada. Falha na renovação derruba a sessão, como no 401. */
export async function refreshAccessToken(): Promise<string> {
  try {
    refreshPromise = refreshPromise ?? doRefresh()
    const access = await refreshPromise
    refreshPromise = null
    return access
  } catch (erro) {
    refreshPromise = null
    forceLogout()
    throw erro
  }
}

// ── Response: normaliza erro + tenta refresh no 401 de token ─────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: string; code?: string }>) => {
    const original = error.config as RetriableConfig | undefined

    // Cancelamento NÃO é falha de rede. O React Query aborta a requisição quando a
    // queryKey muda (o mapa navegou para outra área, o filtro mudou); se isso virasse
    // NETWORK_ERROR a tela mostraria "falha de conexão" por uma chamada que o próprio
    // app descartou. Repassar o erro original deixa o React Query reconhecer o aborto.
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    if (!error.response) {
      // Sem resposta (rede/CORS/timeout)
      return Promise.reject(new ApiError('Falha de conexão com o servidor.', 'NETWORK_ERROR', 0))
    }

    const { status, data } = error.response
    const code = (data?.code ?? 'INTERNAL_ERROR') as ApiError['code']
    const message = data?.error ?? error.message ?? 'Erro inesperado.'

    const isRefreshCall = original?.url?.includes('/auth/refresh')
    // Qualquer 401 numa chamada normal, havendo refresh salvo, tenta renovar o
    // access uma vez. Cobre o token expirado/inválido E o bootstrap pós-reload
    // (access só em memória → some no reload → 1ª chamada volta "Missing
    // Authorization Header"). Sessão revogada (troca de senha) não renova: cai fora.
    const canRefresh =
      status === 401 &&
      code !== 'TOKEN_REVOKED' &&
      !isRefreshCall &&
      Boolean(tokenStore.getRefresh())

    if (canRefresh && original && !original._retry) {
      original._retry = true
      try {
        refreshPromise = refreshPromise ?? doRefresh()
        const access = await refreshPromise
        refreshPromise = null
        original.headers.Authorization = `Bearer ${access}`
        return apiClient(original)
      } catch {
        refreshPromise = null
        forceLogout()
        return Promise.reject(new ApiError('Sessão expirada. Entre novamente.', 'TOKEN_EXPIRED', 401))
      }
    }

    // Sessão revogada (troca de senha) → derruba direto.
    if (status === 401 && code === 'TOKEN_REVOKED') {
      forceLogout()
    }

    return Promise.reject(new ApiError(message, code, status, data))
  },
)

/** Desembrulha o envelope { data } e devolve só o payload. */
export const http = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const r = await apiClient.get<Envelope<T>>(url, config)
    return r.data.data
  },
  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const r = await apiClient.post<Envelope<T>>(url, body, config)
    return r.data.data
  },
  async patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const r = await apiClient.patch<Envelope<T>>(url, body, config)
    return r.data.data
  },
  /** Para o recurso cuja identidade é a CHAVE, e não um id gerado: o lançamento de
   *  diária é o par pessoa mais dia, e reenviar o mesmo corpo produz o mesmo
   *  estado, tenha ele sido criado agora ou corrigido. */
  async put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const r = await apiClient.put<Envelope<T>>(url, body, config)
    return r.data.data
  },
  async del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const r = await apiClient.delete<Envelope<T>>(url, config)
    return r.data.data
  },
}
